import { scrollTopSmooth } from './scroll-to-top';

import type { TranslationKey } from '@shared/constants/translations';
import type { Nullable } from '@shared/types/utils'; // Navigation component height

const HOTEL_PRICES_ANCHOR = 'hotel-details-hotel-prices-section';
const HOTEL_REVIEWS_ANCHOR = 'hotel-details-hotel-reviews-section';
const HOTEL_LOCATION_ANCHOR = 'hotel-details-location-section';
const GOOD_TO_KNOW_ANCHOR = 'hotel-details-good-to-know-section';
const FAQS_ANCHOR = 'hotel-details-faqs-section';
const AMENITIES_ANCHOR = 'hotel-details-amenities-section';
const MEDIA_GRID_ANCHOR = 'hotel-details-media-grid';
const ROOM_SELECTION_ANCHOR = 'hotel-details-room-selection';

const HOTEL_PRICES = 'hotelPrices';
const HOTEL_MEDIA = 'hotelMedia';
const GOOD_TO_KNOW = 'goodToKnow';
const FAQS = 'faqs';
const REVIEWS = 'reviews';
const AMENITIES = 'amenities';
const LOCATION = 'location';
const HEADER_HEIGHT = 40;
const NAVIGATION_HEIGHT = 42;
const NAVIGATION_WITH_FILTER_HEIGHT = 200;
const HEADER_NAVIGATION_HEIGHT = HEADER_HEIGHT + NAVIGATION_HEIGHT;
const NAVIGATION_CONTAINER_HEIGHT = 80;

export type NavigationItems = Array<{
  id: string;
  anchorName: string;
  isShow: boolean;
  text: TranslationKey;
  mobile?: boolean;
}>;

const NAVIGATION_ITEMS: NavigationItems = [
  {
    id: HOTEL_MEDIA,
    anchorName: MEDIA_GRID_ANCHOR,
    text: 'OTARates_label_overView',
    isShow: true,
  },
  {
    id: HOTEL_PRICES,
    anchorName: HOTEL_PRICES_ANCHOR,
    text: 'Filters_label_deals',
    isShow: true,
    mobile: true,
  },
  {
    id: REVIEWS,
    anchorName: HOTEL_REVIEWS_ANCHOR,
    text: 'Reviews_label_reviews',
    isShow: true,
    mobile: true,
  },
  {
    id: LOCATION,
    anchorName: HOTEL_LOCATION_ANCHOR,
    text: 'Location_label_title',
    isShow: true,
  },
  {
    id: GOOD_TO_KNOW,
    anchorName: GOOD_TO_KNOW_ANCHOR,
    text: 'Navigation_label_Details',
    isShow: true,
    mobile: true,
  },
  {
    id: AMENITIES,
    anchorName: AMENITIES_ANCHOR,
    text: 'Filters_label_amenities',
    isShow: true,
  },
];

const SEO_NAVIGATION_ITEMS: NavigationItems = [
  {
    id: HOTEL_PRICES,
    anchorName: HOTEL_PRICES_ANCHOR,
    text: 'OnPageNavigation_HDP_rooms_and_prices',
    isShow: true,
    mobile: true,
  },
  {
    id: AMENITIES,
    anchorName: AMENITIES_ANCHOR,
    text: 'Filters_label_amenities',
    isShow: true,
  },
  {
    id: REVIEWS,
    anchorName: HOTEL_REVIEWS_ANCHOR,
    text: 'OnPageNavigation_HDP_ratings_and_reviews',
    isShow: true,
    mobile: true,
  },
  {
    id: LOCATION,
    anchorName: HOTEL_LOCATION_ANCHOR,
    text: 'OnPageNavigation_HDP_explore_the_area',
    isShow: true,
  },
  {
    id: FAQS,
    anchorName: FAQS_ANCHOR,
    text: 'CityPage_label_faqs',
    isShow: true,
    mobile: true,
  },
];

const scrollTo = (
  element: Nullable<HTMLElement>,
  behavior: string = 'smooth',
  navigationHeight: number = HEADER_NAVIGATION_HEIGHT,
) => {
  const offsetTop = element?.offsetTop ?? 0;

  const slideHeight = offsetTop - navigationHeight;
  if (behavior === 'smooth') {
    scrollTopSmooth(slideHeight);
  } else {
    window.scrollTo(0, slideHeight);
  }
};

const scrollToElement = (
  anchorName: string,
  height: number = HEADER_NAVIGATION_HEIGHT,
) => {
  const anchorElement = document.getElementById(anchorName);
  if (anchorElement) {
    scrollTo(anchorElement, 'smooth', height);
  }
};

// The offsetTop of review reduce 80(navigation height) during scroll and need to scroll the bottom of fixed navigation, so here double navigation height
const scrollToHotelRating = () => {
  scrollToElement(HOTEL_REVIEWS_ANCHOR, 2 * NAVIGATION_CONTAINER_HEIGHT);
  const focusElement = document.getElementById('categoryScore_0');
  if (focusElement) {
    setTimeout(() => {
      focusElement.focus({ preventScroll: true });
    }, 10);
  }
};

const scrollToHotelPrices = () => scrollToElement(HOTEL_PRICES_ANCHOR);

const scrollToRoomGroupingResult = () =>
  scrollToElement(ROOM_SELECTION_ANCHOR, NAVIGATION_WITH_FILTER_HEIGHT);

export {
  HOTEL_MEDIA,
  HOTEL_PRICES,
  NAVIGATION_ITEMS,
  SEO_NAVIGATION_ITEMS,
  GOOD_TO_KNOW,
  GOOD_TO_KNOW_ANCHOR,
  FAQS,
  FAQS_ANCHOR,
  HOTEL_REVIEWS_ANCHOR,
  HOTEL_PRICES_ANCHOR,
  HOTEL_LOCATION_ANCHOR,
  ROOM_SELECTION_ANCHOR,
  LOCATION,
  REVIEWS,
  AMENITIES,
  scrollTo,
  scrollToElement,
  scrollToHotelRating,
  scrollToHotelPrices,
  scrollToRoomGroupingResult,
};
